/**
 * Guides Store (Zustand)
 *
 * Centralized store for per-page user guides.
 * The Firestore listener is set up ONCE in AuthenticatedLayout via useGuidesListener,
 * and all components read from this store via useGuides.
 *
 * This follows the same pattern as announcementsStore.ts — a single global listener
 * populates the store, eliminating redundant Firestore subscriptions.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { PageGuide } from '../../guides-react/types';

interface GuidesState {
  /** All guide documents from Firestore */
  guides: PageGuide[];

  /** Loading state */
  isLoading: boolean;

  /** Error message if listener fails */
  error: string | null;

  // Actions — called by useGuidesListener
  setGuides: (guides: PageGuide[]) => void;
  setIsLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Selectors
  /** Check if a published guide exists for a given pageId */
  hasPublishedGuide: (pageId: string) => boolean;
  /** Get the guide for a given pageId (published or not) */
  getGuide: (pageId: string) => PageGuide | undefined;
  /** Get all published guides */
  getPublishedGuides: () => PageGuide[];
}

export const useGuidesStore = create<GuidesState>()(
  devtools(
    (set, get) => ({
      // Initial state
      guides: [],
      isLoading: true,
      error: null,

      // Actions
      setGuides: (guides) => set({ guides }),
      setIsLoading: (loading) => set({ isLoading: loading }),
      setError: (error) => set({ error }),

      // Selectors
      hasPublishedGuide: (pageId: string) => {
        const { guides } = get();
        return guides.some((g) => g.id === pageId && g.published);
      },

      getGuide: (pageId: string) => {
        const { guides } = get();
        return guides.find((g) => g.id === pageId);
      },

      getPublishedGuides: () => {
        const { guides } = get();
        return guides.filter((g) => g.published);
      },
    }),
    { name: 'GuidesStore' }
  )
);
